import { useState, useEffect, useMemo } from 'react'
import * as LucideIcons from 'lucide-react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import useCategoryStore from '../../stores/categoryStore'
import useSurveyStore from '../../stores/surveyStore'

// Map icon names to Lucide components
const getIcon = (iconName) => {
  const iconMap = {
    'home': LucideIcons.Home,
    'trees': LucideIcons.Trees,
    'warehouse': LucideIcons.Warehouse,
    'utensils': LucideIcons.Utensils,
    'sofa': LucideIcons.Sofa,
    'bed-double': LucideIcons.BedDouble,
    'bed': LucideIcons.Bed,
    'bath': LucideIcons.Bath,
    'shower-head': LucideIcons.ShowerHead,
    'door-open': LucideIcons.DoorOpen,
    'door-closed': LucideIcons.DoorClosed,
    'flower-2': LucideIcons.Flower2,
    'car': LucideIcons.Car,
    'gauge': LucideIcons.Gauge,
    'folder': LucideIcons.Folder,
    'camera': LucideIcons.Camera,
    'image': LucideIcons.Image,
    'building': LucideIcons.Building,
    'hammer': LucideIcons.Hammer,
    'thermometer': LucideIcons.Thermometer,
    'thermometer-sun': LucideIcons.ThermometerSun,
    'droplets': LucideIcons.Droplets,
    'droplet': LucideIcons.Droplet,
    'factory': LucideIcons.Factory,
    'square': LucideIcons.Square,
    'triangle': LucideIcons.Triangle,
    'layers': LucideIcons.Layers,
    'layout': LucideIcons.Layout,
    'grid-3x3': LucideIcons.Grid3X3,
    'flame': LucideIcons.Flame,
    'cabinet': LucideIcons.Archive,
    'zap': LucideIcons.Zap,
    'fuel': LucideIcons.Fuel,
    'pipette': LucideIcons.Pipette,
    'settings': LucideIcons.Settings
  }
  return iconMap[iconName] || LucideIcons.Folder
}

// Section tab labels
const SECTION_LABELS = {
  D: 'Outside',
  E: 'Inside',
  F: 'Services',
  G: 'Grounds',
  X: 'Custom'
}

// Section definitions
const SECTIONS = [
  { code: 'D', name: 'Outside the property', order: 1 },
  { code: 'E', name: 'Inside the property', order: 2 },
  { code: 'F', name: 'Services', order: 3 },
  { code: 'G', name: 'Grounds', order: 4 }
]

export default function CategorySelector({
  selectedCategory,
  onSelectCategory
}) {
  const loading = useCategoryStore(state => state.loading)
  const customCategories = useCategoryStore(state => state.customCategories)
  const getCategoriesBySection = useCategoryStore(state => state.getCategoriesBySection)
  const loadCategories = useCategoryStore(state => state.loadCategories)
  const photoCounts = useSurveyStore(state => state.getPhotoCounts())
  const [activeSection, setActiveSection] = useState('D')
  const [expandedSections, setExpandedSections] = useState(['D'])

  // Load categories (including custom) on mount
  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  // Build sections with their categories (including custom categories assigned to sections)
  // Re-computed when customCategories changes
  const sections = useMemo(() => {
    const builtSections = SECTIONS.map(section => ({
      ...section,
      categories: getCategoriesBySection(section.code)
    }))

    // Get standalone custom categories (no section assigned)
    const standalone = customCategories
      .filter(c => !c.section)
      .map(c => ({
        code: c.code,
        name: c.name,
        icon: c.icon,
        section: 'X',
        aiPrompt: c.aiPrompt,
        isCustom: true
      }))

    // Add standalone custom categories as a separate section if any exist
    if (standalone.length > 0) {
      builtSections.push({
        code: 'X',
        name: 'Custom Categories',
        order: 5,
        categories: standalone
      })
    }

    return builtSections
  }, [customCategories, getCategoriesBySection])

  // Get total photo count for a section
  const getSectionPhotoCount = (sectionCategories) => {
    return sectionCategories.reduce((total, cat) => total + (photoCounts[cat.code] || 0), 0)
  }

  // Toggle accordion section
  const toggleSection = (sectionCode) => {
    setExpandedSections(prev =>
      prev.includes(sectionCode)
        ? prev.filter(s => s !== sectionCode)
        : [...prev, sectionCode]
    )
  }

  // Category button component - Desktop (compact) vs Mobile (full width with name)
  const CategoryButton = ({ category, isCompact = false }) => {
    const Icon = getIcon(category.icon)
    const count = photoCounts[category.code] || 0
    const isSelected = selectedCategory?.code === category.code

    // Mobile: full width button with code + name
    if (isCompact) {
      return (
        <button
          key={category.id}
          onClick={() => onSelectCategory(category)}
          className={`
            w-full flex items-center gap-2 p-2.5 rounded-lg transition-all duration-200
            ${isSelected
              ? 'bg-primary-700 text-white shadow-md'
              : 'bg-white text-slate-600 border border-slate-200 hover:border-primary-300 hover:bg-primary-50'
            }
          `}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold text-sm">{category.code}</span>
          <span className={`text-sm flex-1 text-left truncate ${isSelected ? 'text-white/80' : 'text-slate-500'}`}>
            {category.name}
          </span>
          {count > 0 && (
            <span className={`
              flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold
              ${isSelected ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'}
            `}>
              {count}
            </span>
          )}
        </button>
      )
    }

    // Desktop: compact button with just code
    return (
      <button
        key={category.id}
        onClick={() => onSelectCategory(category)}
        title={`${category.code} - ${category.name}`}
        className={`
          relative flex items-center gap-1.5 rounded-lg transition-all duration-200 p-2 text-xs
          ${isSelected
            ? 'bg-primary-700 text-white shadow-md'
            : 'bg-white text-slate-600 border border-slate-200 hover:border-primary-300 hover:bg-primary-50'
          }
        `}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium truncate">
          {category.code}
        </span>
        {count > 0 && (
          <span className={`
            flex-shrink-0 px-1.5 py-0.5 rounded-full text-xs font-bold
            ${isSelected ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'}
          `}>
            {count}
          </span>
        )}
      </button>
    )
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400">
        <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mr-2" />
        Loading categories...
      </div>
    )
  }

  // Show empty state if no categories loaded
  const totalCategories = sections.reduce((sum, s) => sum + s.categories.length, 0)
  if (totalCategories === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>No categories available.</p>
        <p className="text-sm mt-1">Please refresh the page or clear browser data.</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Desktop/Tablet: Tabs */}
      <div className="hidden md:block">
        {/* Section Tabs */}
        <div className="flex border-b border-slate-200 mb-4">
          {sections.map((section) => {
            const sectionCount = getSectionPhotoCount(section.categories)
            const isActive = activeSection === section.code

            return (
              <button
                key={section.code}
                onClick={() => setActiveSection(section.code)}
                className={`
                  flex items-center gap-2 px-4 py-3 font-medium text-sm
                  border-b-2 transition-colors
                  ${isActive
                    ? 'border-primary-700 text-primary-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }
                `}
              >
                <span>{SECTION_LABELS[section.code]}</span>
                {sectionCount > 0 && (
                  <span className={`
                    px-1.5 py-0.5 rounded-full text-xs
                    ${isActive ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'}
                  `}>
                    {sectionCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Category Grid for Active Section */}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
          {sections
            .find(s => s.code === activeSection)
            ?.categories.map((category) => (
              <CategoryButton key={category.code} category={category} />
            ))}
        </div>
      </div>

      {/* Mobile: Accordion */}
      <div className="md:hidden space-y-2">
        {sections.map((section) => {
          const sectionCount = getSectionPhotoCount(section.categories)
          const isExpanded = expandedSections.includes(section.code)

          return (
            <div key={section.code} className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.code)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                  <span className="font-medium text-slate-700">
                    {section.name}
                  </span>
                </div>
                {sectionCount > 0 && (
                  <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-bold">
                    {sectionCount}
                  </span>
                )}
              </button>

              {/* Category List */}
              {isExpanded && (
                <div className="p-2 space-y-1 bg-white">
                  {section.categories.map((category) => (
                    <CategoryButton
                      key={category.code}
                      category={category}
                      isCompact={true}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Dropdown version for forms
export function CategoryDropdown({
  value,
  onChange,
  label = 'Category',
  error,
  required = false
}) {
  const getCategoriesBySection = useCategoryStore(state => state.getCategoriesBySection)
  const getStandaloneCategories = useCategoryStore(state => state.getStandaloneCategories)

  // Build sections with categories including custom
  const sections = SECTIONS.map(section => ({
    ...section,
    categories: getCategoriesBySection(section.code)
  }))

  // Get standalone categories
  const standaloneCategories = getStandaloneCategories()

  // Get all categories for lookup
  const allCategories = [
    ...sections.flatMap(s => s.categories),
    ...standaloneCategories.map(c => ({
      code: c.code,
      name: c.name,
      icon: c.icon,
      section: null,
      aiPrompt: c.aiPrompt,
      isCustom: true
    }))
  ]

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <select
        value={value || ''}
        onChange={(e) => {
          const category = allCategories.find(c => c.code === e.target.value)
          onChange(category)
        }}
        className={`
          w-full px-4 py-3 pr-10
          appearance-none
          border rounded-lg
          ${error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-primary-500 focus:border-primary-500'}
          focus:outline-none focus:ring-2
          transition-colors duration-200
          bg-white
        `}
      >
        <option value="">Select a category</option>
        {sections.map((section) => (
          <optgroup key={section.code} label={section.name}>
            {section.categories.map((category) => (
              <option key={category.code} value={category.code}>
                {category.code} - {category.name}
              </option>
            ))}
          </optgroup>
        ))}
        {standaloneCategories.length > 0 && (
          <optgroup label="Custom Categories">
            {standaloneCategories.map((category) => (
              <option key={category.code} value={category.code}>
                {category.code} - {category.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {error && (
        <p className="mt-1.5 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}
